import { ActiveRecordSource } from './ActiveRecordSource.js';
import { ActiveRecordStatus } from './ActiveRecordStatus.js';
/**
 * Each instance of {@link ActiveRecord} has access to the core four CRUD
 * operations ({@link ActiveRecord#create Create},
 * {@link ActiveRecord#retrieve Read}, {@link ActiveRecord#update Update},
 * {@link ActiveRecord#delete Delete}) as well as class-level query methods
 * ({@link ActiveRecord.where Where}, {@link ActiveRecord.count Count},
 * {@link ActiveRecord.exists Exists}, {@link ActiveRecord.find Find},
 * {@link ActiveRecord.one One}, {@link ActiveRecord.all All}).
 *
 * @property {ActiveRecordStatus} $status Maintains status indicators while
 *   performing CRUD interactions
 * @property {null|number|string} $primaryKey Uniform access of any record's
 *   unique identifier
 */
export class ActiveRecord {
    constructor(props) {
        this.$status = new ActiveRecordStatus();
        this.initialize(props);
    }
    get $primaryKey() {
        const Record = this.constructor;
        const { primaryKeyPropertyName } = Record.$source;
        return this[primaryKeyPropertyName] || null;
    }
    /**
     * Called in "afterRequest" hook for CRUD interaction with the
     * response from the source.
     * @param {object} props
     */
    initialize(props) {
        Object.assign(this, props);
    }
    /**
     * Create a new record based on the state of this instance.
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord>}
     */
    create(params, opts) {
        this.$status.isSaving = true;
        const actionType = 'CREATE';
        this.beforeRequest(actionType);
        const Record = this.constructor;
        return Record.$source.create(this, params, opts)
            .then((result) => {
            this.afterRequest(actionType, result, false);
            return this;
        })
            .catch((error) => {
            this.afterRequest(actionType, error, true);
            return Promise.reject(error);
        })
            .finally(() => {
            this.$status.isSaving = false;
        });
    }
    /**
     * Retrieve the record corresponding to the primary key of this record.
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord>}
     */
    retrieve(params, opts) {
        this.$status.isLoading = true;
        const actionType = 'RETRIEVE';
        this.beforeRequest(actionType);
        const Record = this.constructor;
        return Record.$source.retrieve(this.$primaryKey, params, opts)
            .then((result) => {
            this.afterRequest(actionType, result, false);
            return this;
        })
            .catch((error) => {
            this.afterRequest(actionType, error, true);
            return Promise.reject(error);
        })
            .finally(() => {
            this.$status.isLoading = false;
        });
    }
    /**
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord>}
     */
    update(params, opts) {
        this.$status.isSaving = true;
        const actionType = 'UPDATE';
        this.beforeRequest(actionType);
        const Record = this.constructor;
        return Record.$source.update(this, params, opts)
            .then((result) => {
            this.afterRequest(actionType, result, false);
            return this;
        })
            .catch((error) => {
            this.afterRequest(actionType, error, true);
            return Promise.reject(error);
        })
            .finally(() => {
            this.$status.isSaving = false;
        });
    }
    /**
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord>}
     */
    delete(params, opts) {
        this.$status.isDeleting = true;
        const actionType = 'DELETE';
        this.beforeRequest(actionType);
        const Record = this.constructor;
        return Record.$source.delete(this.$primaryKey, params, opts)
            .then((result) => {
            this.afterRequest(actionType, result, false);
            return this;
        })
            .catch((error) => {
            this.afterRequest(actionType, error, true);
            return Promise.reject(error);
        })
            .finally(() => {
            this.$status.isDeleting = false;
        });
    }
    /**
     * @see {ActiveRecord#create}
     * @see {ActiveRecord#update}
     */
    save(params, opts) {
        return this.$primaryKey ? this.update(params, opts) : this.create(params, opts);
    }
    /**
     * @param {string} actionType
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    beforeRequest(actionType) { } // eslint-disable-line @typescript-eslint/no-empty-function
    /**
     * @param {string} actionType
     * @param {object} result
     * @param {boolean} didFail
     */
    afterRequest(actionType, result, didFail) {
        !didFail && !(result instanceof Error) && this.initialize(result);
    }
    /**
     * Does {this} instance and the provided instance represent the same record
     * @param {ActiveRecord} comparedTo
     * @return {boolean}
     */
    isEqual(comparedTo) {
        const isNotDefined = comparedTo === null;
        const isNotActiveRecord = !(comparedTo instanceof ActiveRecord);
        if (isNotDefined || isNotActiveRecord)
            return false;
        // TODO: Consider consequence of someone defining $source as getter
        const thisSource = this.constructor.$source;
        const thatSource = comparedTo.constructor.$source;
        const matchesSource = thisSource === thatSource;
        const matchesPrimaryKey = this.$primaryKey === comparedTo.$primaryKey;
        return matchesSource && matchesPrimaryKey;
    }
    /**
     * Create a new record with the provided props.
     * @param {ActiveRecordProps} props Properties of the object to provide as the request body
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord>}
     */
    static create(props, params, opts) {
        const record = new this(props);
        return record.create(params, opts);
    }
    /**
     * Retrieve the single record that matches the provided primary key.
     * @param {number|string} primaryKey
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord>}
     */
    static retrieve(primaryKey, params, opts) {
        const { primaryKeyPropertyName } = this.$source;
        const record = new this({ [primaryKeyPropertyName]: primaryKey });
        return record.retrieve(params, opts);
    }
    /**
     * Update the record represented by the provided primary key with the changes
     * included in the props.
     * @param {ActiveRecordProps} props Properties of the object to provide as the request body
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord>}
     */
    static update(props, params, opts) {
        const record = new this(props);
        return record.update(params, opts);
    }
    /**
     * Delete the record specified by the provided primary key.
     * @param {number|string} primaryKey
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord>}
     */
    static delete(primaryKey, params, opts) {
        const { primaryKeyPropertyName } = this.$source;
        const record = new this({ [primaryKeyPropertyName]: primaryKey });
        return record.delete(params, opts);
    }
    /**
     * Locate records matching the provided params and return the corresponding
     * list. There are no assurances that the response will include ALL results.
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord[]>}
     */
    static where(params, opts) {
        return this.$source.where(params, opts)
            .then((results) => results.map((props) => new this(props)));
    }
    /**
     * Count the number of records matching the provided params.
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<number>}
     */
    static count(params, opts) {
        const pagination = this.paginate(params, opts);
        return pagination.first().then(() => pagination.meta.totalRecords);
    }
    /**
     * Check that at least a single record matches the provided params.
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<boolean>}
     */
    static exists(params, opts) {
        return this.count(params, opts).then((count) => count > 0);
    }
    /**
     * Retrieve the first record that matches the provided params.
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord|null>}
     */
    static find(params, opts) {
        return this.where(params, opts).then((records) => (records.length > 0 ? records[0] : null));
    }
    /**
     * Look for records matching the provided params. Throw an error if anything
     * besides one record is returned.
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord>}
     */
    static one(params, opts) {
        // TODO: Log if request include perPage
        return this.where(params, opts)
            .then((records) => {
            const [record] = records;
            const error = new Error(`"${this.name}.one" returned ${records.length} records instead of 1`);
            return records.length === 1 ? record : Promise.reject(error);
        });
    }
    /**
     * Retrieve all records that match the provided criteria as interpreted by the
     * API. If more than one page of records is specified in the response, all
     * pages will be retrieved prior to resolving the promise.
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {Promise<ActiveRecord[]>}
     */
    static all(params, opts) {
        return this.$source.all(this, params, opts);
    }
    /**
     * @param {object} [params] Object to turn into a query string for the request
     * @param {object} [opts] Options to override request defaults
     * @return {ActiveRecordPagination}
     */
    static paginate(params, opts) {
        return this.$source.paginate(this, params, opts);
    }
}
/**
 * @private
 * @param {ActiveRecord} Record
 * @param {ActiveRecordSource} source
 */
function assignSource(Record, source) {
    Object.defineProperty(Record, '$source', {
        value: source,
        configurable: true,
        writable: true,
    });
}
/**
 * @private
 * @param {ActiveRecord} Record
 * @throws {Error}
 */
function defineBasicSource(Record) {
    if (typeof Record.$AR.url !== 'string') {
        throw new Error('You must define a "$source" or "$AR.url" on an ActiveRecord implementation');
    }
    const SourceClass = Record.$AR.SourceClass || ActiveRecordSource;
    return new SourceClass(Record.$AR.url, Record.$AR.options);
}
/* Allow passive configuration of the "$source" property
 * Initiates configuration on first attempted access if not already configured.
 * The self-configuration is bypassed by assigning a source to the property.
 */
Object.defineProperty(ActiveRecord, '$source', {
    // Allow another property descriptor to overwrite this one
    configurable: true,
    get() {
        const source = defineBasicSource(this);
        assignSource(this, source);
        return source;
    },
    set(value) {
        if (!(value instanceof ActiveRecordSource)) {
            throw new Error(`"${this.name}.$source" must be an instance of ActiveRecordSource`);
        }
        assignSource(this, value);
    },
});
export default ActiveRecord;
